import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageProps, User } from "../types/auth";

import styles from './styles/MemberAccomplishmentPage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Member extends User {
  role: string;
}

interface Accomplishment {
  accomplishment_id: number;
  id: number;
  name: string;
}

interface RouteParams {
  [key: string]: string | undefined;
  member_id?: string;
}

const MemberAccomplishmentPage: React.FC<PageProps> = ({ token, selectedOrgID }) => {
  const [memberInfo, setMemberInfo] = useState<Member | null>(null);
  const [availableAccomplishments, setAvailableAccomplishments] = useState<Accomplishment[]>([]);
  const [error, setError] = useState<string>("");
  const navigate = useNavigate();
  const { member_id } = useParams<RouteParams>();

  useEffect(() => {
    const fetchMemberInfo = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/users/${member_id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch member information.");
          return;
        }

        const memberData: Member = await response.json();
        setMemberInfo(memberData);
      } catch (err) {
        setError("An unexpected error occurred.");
      }
    };

    const fetchAccomplishments = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/accomplishments`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch accomplishments.");
          return;
        }

        const accomplishments: Accomplishment[] = await response.json();
        setAvailableAccomplishments(accomplishments || []);
      } catch (err) {
        setError("An unexpected error occurred.");
      }
    };

    if (member_id) {
      fetchMemberInfo();
      fetchAccomplishments();
    }
  }, [member_id, token, selectedOrgID]);

  const handleGrant = async (accomplishmentID: number) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/accomplishments/user/${member_id}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            accomplishment_id: accomplishmentID,
          }),
        }
      );

      if (!response.ok) {
        console.error("Failed to grant accomplishment.");
        return;
      }

      navigate(`/members/${member_id}`);
    } catch (err) {
      console.error("Error granting accomplishment:", err);
    }
  };

  if (error) {
    return <p className={styles.errorMessage}>{error}</p>;
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Grant Accomplishment</h1>
      <div className={styles.memberDetails}>
        <strong>Member:</strong>{" "}
        <a href={`/members/${member_id}`} className={styles.memberLink}>
          {memberInfo?.display_name
            ? `${memberInfo.display_name} (${memberInfo.username})`
            : memberInfo?.username}
        </a>
      </div>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.tableHeader}>Accomplishment</th>
            <th className={styles.tableHeader}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {availableAccomplishments.length > 0 ? (
            availableAccomplishments.map((accomplishment) => (
              <tr key={accomplishment.id} className={styles.tableRow}>
                <td className={styles.tableCell}>{accomplishment.name}</td>
                <td className={styles.tableCell}>
                  <button
                    className={styles.grantButton}
                    onClick={() => handleGrant(accomplishment.accomplishment_id)}
                  >
                    Grant
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={2} className={styles.tableCell}>No available accomplishments.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default MemberAccomplishmentPage;
